// src/ui/controls/TorchButton.tsx — VIS-03 / D-19..D-22 on-screen torch toggle.
//
// Mirror of src/ui/controls/Joystick.tsx — React-DOM overlay on top of the
// Phaser canvas. Writes a one-shot torchToggle flag to the shared
// LocalInputController singleton (localInputController.requestTorchToggle()).
// The sim consumes + auto-clears inside decide() — see
// src/sim/controllers/local-input-controller.ts.
//
// Visual state (data-state + aria-pressed) DERIVES from the sim via
// renderBus 'torch-flicked' events — preserves the FOUND-04 one-way data
// flow. The button never owns or predicts torchOn locally.
//
// jsdom-compatible inline style mirror of torch-button.css so Vitest asserts
// layout shape without stylesheet parsing (PATTERNS.md Pattern E — same
// convention used by Joystick.tsx).
//
// P3 Plan 03-08 extension: role gate (D-21 deferred from P2). The button
// renders null when self.role !== 'hunter' so hiders don't see an
// inapplicable torch control. Role is read from the sim-tick payload.
//
// Source: Phase 2 UI-SPEC.md §HUD Component Inventory §TorchButton;
// PATTERNS.md §TorchButton.tsx.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type JSX,
  type PointerEvent as ReactPointerEvent,
} from 'react';

import { renderBus } from '../../render/bus';
import { localInputController } from '../../sim/controllers/registry';

import './torch-button.css';

/** The local player's entity id. Matches the sim-side convention in tick.ts. */
const PLAYER_ENTITY_ID = 'e0';

export function TorchButton(): JSX.Element | null {
  const [torchOn, setTorchOn] = useState(false);
  // P3 Plan 03-08: role gate — torch is a hunter-only control. Default to
  // 'hunter' so the button stays visible during boot until the first
  // sim-tick arrives (no flash-of-disappearing-button on hunter sessions).
  const [role, setRole] = useState<'hunter' | 'hider'>('hunter');
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const off = renderBus.on('torch-flicked', (e) => {
      // Entity isolation: ignore torch events for other entities (e.g. AI bots
      // or future multi-player hunters). Only the local player's state drives
      // this button's visual — T-02-13 mitigation.
      if (e.entityId !== PLAYER_ENTITY_ID) return;
      setTorchOn(e.on);
    });
    // P3 Plan 03-08: subscribe to sim-tick for the player's current role.
    // narrow: hunter|hider only — dogs/adults never control the torch button.
    const offTick = renderBus.on('sim-tick', (e) => {
      setRole(e.playerRole === 'hunter' ? 'hunter' : 'hider');
    });
    return () => {
      off();
      offTick();
    };
  }, []);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>) => {
      // preventDefault stops iOS from synthesising a click or firing a focus
      // ring on the tap; also prevents double-fire on touch devices that
      // follow pointerdown with a compatibility mousedown.
      e.preventDefault();
      localInputController.requestTorchToggle();
    },
    [],
  );

  // P3 Plan 03-08 — role gate: torch is hunter-only. MUST sit below every
  // hook call so the number of hooks stays stable across renders when the
  // role flips mid-session (Rules-of-Hooks: early returns can never shadow
  // a hook).
  if (role !== 'hunter') return null;

  return (
    <button
      ref={buttonRef}
      type="button"
      className="torch-button"
      aria-label="Toggle torch"
      aria-pressed={torchOn}
      data-state={torchOn ? 'on' : 'off'}
      data-testid="torch-button"
      onPointerDown={onPointerDown}
      // Inline-style mirror of torch-button.css so jsdom tests can assert
      // layout without CSS loading. PATTERNS.md Pattern E — same pattern as
      // Joystick.tsx. CSS is canonical for prod; inline style is test-only.
      style={{
        position: 'fixed',
        bottom: 'calc(env(safe-area-inset-bottom) + 16px)',
        right: 'calc(env(safe-area-inset-right) + 16px)',
        width: '64px',
        height: '64px',
        borderRadius: '50%',
        border: torchOn ? '2px solid #c98d28' : '2px solid #2a4a62',
        background: torchOn ? '#e8a838' : 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        touchAction: 'none',
        pointerEvents: 'auto',
        zIndex: 60,
        cursor: 'pointer',
        userSelect: 'none',
        outline: 'none',
        padding: 0,
        color: torchOn ? '#0d1f2d' : '#e8a838',
      }}
    >
      {/* Inline SVG torch pictograph — UI-SPEC: no external icon library dep.
          Stroke-only in the off state, filled in the on state via the
          .torch-button[data-state='on'] .torch-button__icon CSS rule. The
          32×32 viewBox is centred inside the 64 px button. */}
      <svg
        className="torch-button__icon"
        viewBox="0 0 32 32"
        aria-hidden="true"
        focusable="false"
        width="32"
        height="32"
      >
        {/* Flame: triangle above the torch body. */}
        <path d="M16 4 L12 14 L20 14 Z" />
        {/* Torch body: rectangle with a small cross-hatch at the top lip. */}
        <path d="M12 14 L20 14" />
        <path d="M12 20 L12 28 L20 28 L20 20 Z" />
      </svg>
    </button>
  );
}
